import React, { useCallback, useImperativeHandle } from "react";
import type { Ref } from "react";
import { isFunction } from "@common/helpers/typeGuards";
import { PlaidConnection } from "@common/types/plaidConnection";
import { useInteraction } from "@hooks/useInteraction";
import {
    usePayPalConnectionCreationMutation,
    usePayPalMigrationFromPlaidMutation,
} from "@api/paypal.api";
import {
    ConnectionActionButton,
    ConnectionButtonSize,
    ConnectionButtonVariant,
} from "@components/financialConnection/ConnectionActionButton";
import {
    AddAccountProps,
    CreateAccountHandle,
} from "../accounts/AddAccounts/lib";
import { usePayPalAuthorization } from "./usePayPalAuthorization";

interface Props extends AddAccountProps {
    children?:
        | React.ReactNode
        | ((props: {
              connecting: boolean;
              initializeConnection: () => void;
          }) => React.ReactNode);
    btnVariant?: ConnectionButtonVariant;
    btnSize?: ConnectionButtonSize;
    leftIcon?: React.ReactNode;
    tooltip?: React.ReactNode;
    className?: string;
    plaidConnectionToMigrate?: PlaidConnection;
}
export const CreatePayPalConnection = ({
    children = <>Connect</>,
    onCreated,
    defaultEntity,
    btnVariant = "secondary",
    btnSize = "md",
    leftIcon,
    tooltip,
    className,
    plaidConnectionToMigrate,
    ref,
}: Props & { ref?: Ref<CreateAccountHandle> }) => {
    const connectionCreationMutation =
        usePayPalConnectionCreationMutation(onCreated);
    const migrationMutation = usePayPalMigrationFromPlaidMutation(onCreated);
    const createConnection = useCallback(
        (authCode: string, sharedId: string) => {
            if (plaidConnectionToMigrate) {
                migrationMutation.mutate({
                    plaidConnectionId: plaidConnectionToMigrate.id,
                    authCode,
                    sharedId,
                });
            } else {
                connectionCreationMutation.mutate({
                    authCode,
                    sharedId,
                    defaultEntityId: defaultEntity?.id,
                });
            }
        },
        [
            connectionCreationMutation,
            defaultEntity,
            plaidConnectionToMigrate,
            migrationMutation,
        ],
    );

    const { initializeConnection } = usePayPalAuthorization(createConnection);

    useImperativeHandle(
        ref,
        () => ({
            initializeConnection,
        }),
        [initializeConnection],
    );

    const authorize = useInteraction(initializeConnection);
    const isConnecting =
        connectionCreationMutation.isPending || migrationMutation.isPending;

    if (isFunction(children)) {
        return children({
            connecting: isConnecting,
            initializeConnection,
        });
    }

    return (
        <ConnectionActionButton
            loading={isConnecting}
            variant={btnVariant}
            size={btnSize}
            leftIcon={leftIcon}
            tooltip={tooltip}
            className={className}
            {...authorize}
        >
            {children}
        </ConnectionActionButton>
    );
};
